import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import { getGreenhousePlotData, getExcelData } from "../../../utils/api.js";

/** @typedef {import("./backend.types.d.ts").SimplifiedEnvelope} SimplifiedEnvelope */
/** @typedef {import("./backend.types.d.ts").SimplifiedRowSeries} SimplifiedRowSeries */
/** @typedef {import("./backend.types.d.ts").SimplifiedPoint} SimplifiedPoint */

export function getAllHarvestDates(start, end) {
  if (!start || !end) return [];

  let s = dayjs(start).startOf("day");
  let e = dayjs(end).startOf("day");
  if (!s.isValid() || !e.isValid()) return [];

  if (s.isAfter(e, "day")) [s, e] = [e, s];

  const dates = [];
  for (let d = s; !d.isAfter(e, "day"); d = d.add(1, "day")) {
    dates.push(d.format(BACKEND_DATE_FORMAT));
  }
  return dates;
}

function parseRowNumber(rowLabel) {
  if (typeof rowLabel !== "string") return null;
  const m = rowLabel.match(/row\s*(\d+)/i);
  return m ? Number(m[1]) : null;
}

function isEmptyObject(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0
  );
}

function extractAvg80_95(data) {
  // data can be {}, null, or an object with percentiles and average_length_cm
  if (!data || isEmptyObject(data)) return null;
  const p = data?.percentiles?.["80_95"];
  const val = p?.average_length_cm;
  return Number.isFinite(val) ? Number(val) : null;
}

/* ---------------------------
   Variety indexing helpers
----------------------------*/
function normBayKey(x) {
  return String(x ?? "")
    .trim()
    .toLowerCase();
}

function parseRowRange(block) {
  if (block == null) return null;
  const s = String(block).trim();
  if (!s) return null;
  const cleaned = s.replace(/\s+/g, "");
  if (cleaned.includes("-")) {
    const [a, b] = cleaned.split("-");
    const start = parseInt(a, 10);
    const end = parseInt(b, 10);
    if (Number.isFinite(start) && Number.isFinite(end)) {
      return [Math.min(start, end), Math.max(start, end)];
    }
    return null;
  }
  const n = parseInt(cleaned, 10);
  if (Number.isFinite(n)) return [n, n];
  return null;
}

/**
 * Build: Map<bayKeyNorm, Array<{start,end,variety,plantingTs}>>
 * Input is ALWAYS an object keyed by bay name:
 * {
 *   "W6BAY22": [{ "Row": "1-4", "Variety": "X", "Planting Date": "YYYY-MM-DD", ... }, ...],
 *   "W6BAY26": [ ... ],
 *   ...
 * }
 */
function buildVarietyIndex(excelRowsObj) {
  const idx = new Map();
  const obj =
    excelRowsObj &&
    typeof excelRowsObj === "object" &&
    !Array.isArray(excelRowsObj)
      ? excelRowsObj
      : {};

  for (const [bayKey, list] of Object.entries(obj)) {
    const rows = Array.isArray(list) ? list : [];
    for (const r of rows) {
      const rowField = r?.["Row"];
      const variety = r?.["Variety"] ?? null;
      const plantingDate = r?.["Planting Date"];
      const range = parseRowRange(rowField);
      if (!range) continue;

      const [start, end] = range;
      const plantingTs = dayjs(plantingDate).isValid()
        ? dayjs(plantingDate).valueOf()
        : -Infinity;

      const key = normBayKey(bayKey); // use object key as the bay identifier
      if (!idx.has(key)) idx.set(key, []);
      idx.get(key).push({ start, end, variety, plantingTs });
    }
  }
  return idx;
}

/**
 * Lookup latest variety (by planting date) for a (bay,rowNumber)
 */
function lookupVariety(varIdx, bayKey, rowNumber) {
  if (!Number.isFinite(rowNumber)) return null;
  const list = varIdx.get(normBayKey(bayKey));
  if (!list || !list.length) return null;

  let best = null;
  for (const it of list) {
    if (rowNumber >= it.start && rowNumber <= it.end) {
      if (!best || it.plantingTs > best.plantingTs) best = it;
    }
  }
  return best?.variety ?? null;
}

/**
 * Normalize raw backend -> SimplifiedEnvelope
 * raw:
 * {
 *   "65": { "Row 6": [ {date, data, is_visible, is_harvested}, ... ], ... },
 *   ...
 * }
 */
function normalizeToSimplified(raw, harvestDates, varietyIndex) {
  /** @type {SimplifiedRowSeries[]} */
  const series = [];

  const bays = raw && typeof raw === "object" ? raw : {};
  for (const [bayId, rowsObj] of Object.entries(bays)) {
    const rows = rowsObj && typeof rowsObj === "object" ? rowsObj : {};

    for (const [rowLabel, arr] of Object.entries(rows)) {
      const byDate = new Map();
      let harvestedDate = null;
      if (Array.isArray(arr)) {
        for (const item of arr) {
          if (!item || typeof item !== "object") continue;
          if (typeof item.date !== "string") continue;
          if (item.is_visible === false) continue;
          if (item.is_harvested) harvestedDate = item.date;
          byDate.set(item.date, item.data ?? null);
        }
      }

      /** @type {SimplifiedPoint[]} */
      const points = harvestDates.map((date) => {
        const data = byDate.has(date) ? byDate.get(date) : undefined;

        const isHarvested = date === harvestedDate;
        let avgLeafCm;

        if (
          data === null ||
          isEmptyObject(data) ||
          typeof data === "undefined"
        ) {
          avgLeafCm = null;
        } else {
          avgLeafCm = extractAvg80_95(data);
        }

        /** @type {SimplifiedPoint} */
        return { date, isHarvested, avgLeafCm };
      });

      const rowNumber = parseRowNumber(String(rowLabel));
      const variety = lookupVariety(varietyIndex, String(bayId), rowNumber);

      series.push({
        bayId: String(bayId),
        rowLabel: String(rowLabel),
        rowNumber,
        variety: variety ?? null,
        points,
      });
    }
  }

  return /** @type {SimplifiedEnvelope} */ ({
    dates: harvestDates.slice(), // ascending
    series,
  });
}

export const backendService = {
  /**
   * Fetch and normalize greenhouse plot data to a minimal, stable shape.
   * @param {Object} args
   * @param {string|number} args.orgId
   * @param {string|number} args.siteId
   * @param {string|number} args.cropId
   * @param {string} args.start  // "YYYY-MM-DD"
   * @param {string} args.end    // "YYYY-MM-DD"
   * @param {number} [args.forecastDays]
   * Returns { dates, series } where `series[*].points` always spans `dates`.
   * Each series item now also has `variety` (best-latest match by Bay/Row range).
   */
  async getFilteredGreenhouse({
    orgId,
    siteId,
    cropId,
    start,
    end,
    forecastDays,
  }) {
    const harvestDates = getAllHarvestDates(start, end);

    // 1) Main time-series grid
    const rawEnvelope = await getGreenhousePlotData(
      orgId,
      siteId,
      cropId,
      null,
      harvestDates,
    );
    const raw = rawEnvelope?.data?.data ?? {};

    // 2) Variety sheet needs the greenhouse_names list
    const greenhouseNames = Object.keys(raw).map(String).filter(Boolean);

    let varietyIndex = new Map();
    try {
      const excelResp = await getExcelData(
        orgId,
        siteId,
        cropId,
        null,
        greenhouseNames,
      );
      // rows is ALWAYS an object keyed by bay
      const excelRowsObj = excelResp?.data?.data?.rows || {};
      varietyIndex = buildVarietyIndex(excelRowsObj);
    } catch (e) {
      console.error("Variety fetch/index failed:", e?.message || e);
    }

    return normalizeToSimplified(raw, harvestDates, varietyIndex);
  },
};
